import ApplicationController from './application_controller'

/**
 * Confirm Field Controller - Validates user input before enabling form submission
 *
 * Prevents accidental form submission by requiring the user to either:
 * - Type a specific text string (for text inputs) to match a confirmation phrase
 * - Check all checkboxes (for checkbox inputs) before submitting
 *
 * Common use case: Confirming destructive actions like deleting resources.
 *
 * @example Text Field Confirmation (Delete Confirmation)
 * <form data-controller="confirm-field"
 *       data-confirm-field-matcher-value="App Name">
 *
 *   <input type="text"
 *          data-confirm-field-target="source"
 *          data-action="input->confirm-field#validate"
 *          placeholder="Type application name here">
 *
 *   <button type="submit"
 *           data-confirm-field-target="button">
 *     Delete Application
 *   </button>
 * </form>
 *
 * @example Checkbox Confirmation
 * <form data-controller="confirm-field">
 *   <label>
 *     <input type="checkbox"
 *            data-confirm-field-target="source"
 *            data-action="change->confirm-field#validate">
 *     I understand this action cannot be undone
 *   </label>
 *
 *   <label>
 *     <input type="checkbox"
 *            data-confirm-field-target="source"
 *            data-action="change->confirm-field#validate">
 *     I have backed up my data
 *   </label>
 *
 *   <button type="submit">Proceed</button>
 * </form>
 *
 * @target source - The input field(s) to validate (text input or checkboxes)
 * @target button - Optional specific submit button to enable/disable
 * @value {String} matcher - Expected text value for text input validation
 * @value {Boolean} confirmed - Internal state tracking validation status
 */
export default class extends ApplicationController {
  static targets = ['button', 'source']
  static values = { matcher: String, confirmed: { type: Boolean, default: false } }

  connect () {
    this.#setButtonsState(false)
  }

  validate () {
    this.confirmedValue = this.#isValid
  }

  // private

  confirmedValueChanged () {
    this.confirmedValue
      ? this.#setButtonsState(true)
      : this.#setButtonsState(false)
  }

  #setButtonsState (enabled) {
    (this.hasButtonTarget ? [this.buttonTarget] : this.#submitButtons)
      .forEach(button => { button.disabled = !enabled })
  }

  #textFieldMatch () {
    return this.sourceTarget.value.trim() === this.matcherValue.trim()
  }

  #boxesChecked () {
    return this.sourceTargets.every(checkbox => checkbox.checked)
  }

  get #isValid () {
    return this.sourceTarget.type === 'text'
      ? this.#textFieldMatch()
      : this.#boxesChecked()
  }

  get #submitButtons () {
    return this.element.querySelectorAll('input[type="submit"], button[type="submit"]')
  }
}
